/**
 * 「通过 / 打回」按钮面板 —— **两个视图共用同一个组件**。
 *
 * ## 它修的是什么（2026-10-15 用户实测）
 *
 * 用户原话：
 *
 * > 昨天孩子提交了作业，但我没有检查，就没有点通过，它就过期了，过期是没问题。
 * > 问题在于，我现在检查了作业，我想点通过，**却没有按钮了**。
 *
 * 这个面板原先**只**渲染在「今天的作业」的查看态里（`ParentHomeworkPage` 内部的一个
 * 局部组件）。而「超时」是**跨天的派生视图**：昨天提交、今天才超时的作业项
 * **只会出现在超时列表里** —— 于是家长在唯一看得见它的地方，只有「放弃」可点。
 * 界面上那行字还写着"这一项会在家长点「通过」后从超时里消失"，
 * 于是**界面承诺了一个不存在的按钮**。
 *
 * 修法：把这个面板抽出来，让「今天的作业」的作业项与「超时」行
 * （`components/homework/OverdueRow.tsx`）渲染**同一个组件**、传**同一套回调**
 * （回调来自 `lib/homeworkApprovalActions.tsx` 的 `useHomeworkApprovalActions()`）。
 * ⛔ 不要为了「超时」再写第二份 —— 本项目反复因为"同一规则两处实现"出问题。
 *
 * ## 判据只有一条（与视图无关）
 *
 * `homeworkApprovalActions()`（`@fht/shared`）：`status === 'submitted'` **且**
 * 服务端给的 `canApprove`。所以学生端、只读家长**一个按钮都拿不到**
 * —— 不靠"点了会失败"来保护。
 *
 * ## 「通过 / 打回 / 放弃」**同属一行**（2026-10-15 用户实测的第二次修复）
 *
 * 用户原话：
 *
 * > 那个红色的"放弃"按钮**排在第二行了**，你让他们三个按钮在同一行吧。
 *
 * 根因是"每一组动作各自一个容器"：「通过 / 打回」在**这个**组件的按钮行里，
 * 「放弃」在 `OverdueRow` 的 `mt-3 flex flex-wrap` 里 —— 两个容器，所以**必然**
 * 各占一行，跟屏幕多宽无关。
 *
 * 修法：这一行**只有一个**按钮容器，别的按钮通过 `trailingActions`
 * 作为**同级兄弟**插进来（见下）。⛔ 谁都不要再给"第二组按钮"另起一个行容器 ——
 * 只要按钮行分裂成两个 flex 容器，"排成两行"就会回来，无论怎么调宽度。
 *
 * ## 窄屏不许改回去（本项目修过一次）
 *
 * 外层是 `flex-col`：说明文字**独占一行**，按钮列 `shrink-0`。
 * ⛔ 别给文字列写 `flex-1`：它的 `flex-basis` 是 **0**，于是"文字 + 按钮"
 * 永远能塞进同一行（`flex-wrap` 因此永远不换行），390px 的手机上文字只剩
 * 按钮吃剩下的 105px（≈8.8 字/行）—— 那正是用户报的"挤成几列、几乎不可读"。
 *
 * ⛔ 也别把面板恢复成 `sm:flex-row`：说明文字是**两三行**的中文，而按钮行是
 * **242px 的死宽**（三个按钮 + 两个 gap + 一条分隔线，实测见下）。两者并排需要
 * **≥600px 的内容列**才不至于把文字压成几列 —— 640px 视口（内容列 592px）时
 * 文字只剩 **23px**，正好又踩回同一个坑。所以这里**始终纵向**：文字一行、
 * 按钮行一行。
 *
 * ## 按钮行要"一行放得下"，面板就不能再吃左右内边距（实测）
 *
 * 实测（Edge headless，真机 `px-4` 的内容列）：
 *   - 三个按钮本体 `75.2 + 70.1 + 71.2 = 216.5px`（`h-9 px-3 text-sm`，按钮**没被改小**）；
 *   - `gap-2` ×2 + 1px 分隔线 → 按钮行 **241.6px**（`flex-wrap` 的换行阈值）；
 *   - 320px 视口的内容列 288px，`OverdueRow` 的卡片再吃掉 `p-3` + 2px 边框 → 面板只有 **260px**。
 * 261 > 260 就会**横向溢出**（按钮被卡片裁掉一截），所以面板从 `px-3 py-2`
 * 收成**只留 `py-2`**：260px 放 241.6px 的按钮行，留 18.4px 余量。
 * ⚠️ 这不是"为了强塞一行而缩小按钮"—— 按钮的尺寸、字号、内边距一个字没动，
 * 收的是**面板自己的留白**。⛔ 别把 `px-3` 加回来：一加回来 320px 就放不下。
 *
 * ## 按钮行自己 `flex-wrap`：真的放不下时优雅换行
 *
 * 242px 是本行三个按钮的"一行阈值"。比 320px 还窄的视口（内容列 < 242px 的
 * 极端情况）允许换行 —— 换行**不缩小按钮**、也不横向溢出（已实测 `scrollW == clientW`）。
 * ⚠️ 第三次修复时用 Edge headless 对着真实 `dist` CSS 重测过：三个 `sm` 的按钮行
 * （含两个 `gap-2` 与 1px 分隔线）实测 **234.23px**；数字与上面 241.6px 的估算差值
 * 来自字体度量（按钮宽度是文字撑出来的），**以实测为准**。
 *
 * ## 这一行里的按钮**必须同 size**（2026-10-15 用户实测的第三次修复）
 *
 * 用户原话：
 *
 * > 三个按钮在一行了，但是"放弃"按钮好像比另外两个按钮高哦，你把它改成一样高的，
 * > 这样才好看呐。
 *
 * 根因不是宽度，是**尺寸不一致**：「通过 / 打回」写的是 `size="sm"`（`h-9` = 36px），
 * 而「超时」行传进来的「放弃」是 `size="md"`（`h-11` = 44px）。三者顶端对齐、
 * 底端差 **8px**，看起来就是那个红按钮"高出来一截"。
 *
 * 修法有两步，缺一不可：
 *   1. 尺寸只留**一个**来源：下面的 `APPROVAL_ACTION_BUTTON_SIZE`。
 *      这个文件里两个按钮与 `OverdueRow` 传进来的 `trailingActions` 读的是同一个值，
 *      所以"同行不同高"再也写不出来；
 *   2. 统一到**小的那一档**（`sm` = 36px），不是大的。实测：320px 视口下面板只有
 *      **260px** 宽，三个按钮全 `md` 时按钮行要 **272.97px** → `flex-wrap` 立刻把
 *      「放弃」挤到第二行（就是上一轮刚修掉的那个 bug）；三个 `sm` 只要 **234.23px**，
 *      稳在一行。
 *
 * ⛔ 以后往这个按钮行里再加按钮（或换某个按钮的 size）时，**必须**继续用
 * `APPROVAL_ACTION_BUTTON_SIZE`：⛔ 不要写 `size="md"` / `size="lg"` 这样的字面量。
 * ⛔ 也不要为了"统一"把「放弃」的 `variant="danger"` 改掉 —— 红色是"放弃/删除类操作"
 * 的信号，这次只统一**尺寸**，颜色一个字没动。
 */
import type { ReactNode } from 'react';
import { Button } from '../ui/Button';
import type { ButtonSize } from '../ui/Button';
import { IconCheck, IconRotateCcw } from '../ui/Icons';
import {
  homeworkApprovalActions,
  homeworkApprovalPrompt,
  type HomeworkApprovalTarget,
} from '@fht/shared';

/**
 * **这一行里所有按钮的唯一尺寸**（见文件头"必须同 size"那段）。
 *
 * `sm` = `h-9 px-3 text-sm` = 实测 **36px 高**。⛔ 别改成 `md`：三个 `md` 在 320px
 * 视口下（面板 260px）要 272.97px，`flex-wrap` 会把「放弃」挤到第二行。
 *
 * ⚠️ 它是 `export` 的，因为「放弃」按钮住在 `OverdueRow` 里 —— 那个按钮通过
 * `trailingActions` 进这一行，尺寸必须从**这里**读，而不是各自写一个字面量。
 */
export const APPROVAL_ACTION_BUTTON_SIZE: ButtonSize = 'sm';

export interface ApprovalActionsProps {
  /** 只有 `status` / `canApprove` / `pointsPerUnit` 三个字段参与判据与文案。 */
  target: HomeworkApprovalTarget;
  /** 通过 / 打回有一个在飞（两个视图共用同一个 busy）。 */
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  /**
   * **要跟「通过 / 打回」排在同一行**的额外按钮（「超时」行用它把「放弃」放进来）。
   *
   * 它渲染在按钮行**内部**、两个按钮之后，所以"同属一行"是**结构**决定的，
   * 不靠调用点各自记得把容器写成一样的。
   *
   * ⚠️ 只传"已经决定要渲染"的节点（`OverdueRow` 那边用 `showSkip &&` 判断），
   * 这里不再重复一遍显隐判据；传 `null` / `undefined` 等同于没有额外按钮
   * （「今天的作业」那个调用点就是这样，两个按钮的样子一个字都没变）。
   */
  trailingActions?: ReactNode;
}

/** 面板里的一段说明：`strong` 的段落渲染成 `<strong>`（哪一段加粗由 shared 决定）。 */
function PromptSegment({ text, strong }: { text: string; strong: boolean }) {
  return strong ? <strong>{text}</strong> : <>{text}</>;
}

export function ApprovalActions({
  target,
  busy,
  onApprove,
  onReject,
  trailingActions,
}: ApprovalActionsProps) {
  const { showApproveActions } = homeworkApprovalActions(target);
  // ⛔ 唯一的一个提前返回：与视图无关的判据（见文件头）。
  if (!showApproveActions) return null;

  const prompt = homeworkApprovalPrompt(target);

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-xl bg-amber-50 py-2">
      <span className="min-w-0 text-xs font-medium text-amber-800">
        {prompt.segments.map((segment, index) => (
          <PromptSegment key={index} text={segment.text} strong={segment.strong} />
        ))}
      </span>
      {/*
        ⚠️ 按钮列 `shrink-0`（不参与收缩、不被压变形），说明文字那列 `min-w-0`。
        ⛔ 别给文字列写 `flex-1`（`flex-basis: 0` → 会被按钮挤成几列，见文件头）。
        `flex-wrap` 是给"比 320px 还窄"的极端宽度留的退路：换行也**不缩小按钮**。
      */}
      <div className="flex shrink-0 flex-wrap gap-2">
        {/*
          「通过 / 打回」用**矢量图标**（`components/ui/Icons.tsx` 里 vendored 的 Lucide），
          不用 emoji：emoji 是彩色字形、不认 `currentColor`（danger 色染不上），
          宽度还随字形走。按钮本身有可见文字，语义不缺。
          ⚠️ 图标外面那层 `-ml-0.5 mr-1.5` 是间距：`Button` 的图标槽**不带 gap**
          （既有调用点靠 emoji 自带的字距），换成 16px 的矢量图标后必须自己给，
          否则图标会贴着文字。
        */}
        <Button
          size={APPROVAL_ACTION_BUTTON_SIZE}
          loading={busy}
          icon={
            <span className="-ml-0.5 mr-1.5 inline-flex">
              <IconCheck size={16} />
            </span>
          }
          onClick={onApprove}
        >
          通过
        </Button>
        <Button
          size={APPROVAL_ACTION_BUTTON_SIZE}
          variant="secondary"
          disabled={busy}
          icon={
            <span className="-ml-0.5 mr-1.5 inline-flex">
              <IconRotateCcw size={16} />
            </span>
          }
          onClick={onReject}
        >
          打回
        </Button>
        {/*
          ⚠️ 额外按钮（「超时」行传进来的「放弃」）是**这个按钮行的兄弟**，
          不是另起一行 —— 见文件头"同属一行"那段。⛔ 不要把它挪到按钮行外面。

          ⚠️ 它的 `size` 必须用 `APPROVAL_ACTION_BUTTON_SIZE`（`OverdueRow` 那边就是这么传的）：
          "排在同一行"只保证**顶端**对齐，尺寸不同的话底端还是会差一截
          （2026-10-15 用户实测的第三次修复，见文件头"必须同 size"）。
        */}
        {trailingActions}
      </div>
    </div>
  );
}
